import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "../api/axios";
import { ShoppingBag, Star, Heart, Eye, Filter, ChevronRight, Package, Loader2 } from "lucide-react";

const CategoryProducts = () => {
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [wishlist, setWishlist] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const catRes = await axios.get(`/category/${categoryId}`);
        setCategory(catRes.data.category);
        const prodRes = await axios.get(`/product/category/${categoryId}`);
        setProducts(prodRes.data.products || []);
      } catch (err) {
        setError("Failed to load products");
      }
      setLoading(false);
    };
    fetchProducts();
  }, [categoryId]);

  const toggleWishlist = (productId) => {
    setWishlist(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const getSortedProducts = () => {
    let sorted = [...products];
    switch(sortBy) {
      case "price-low":
        return sorted.sort((a, b) => a.price - b.price);
      case "price-high":
        return sorted.sort((a, b) => b.price - a.price);
      case "name":
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return sorted;
    }
  };

  const getFilteredProducts = () => {
    let filtered = getSortedProducts();
    if (priceRange.min) {
      filtered = filtered.filter(p => p.price >= Number(priceRange.min));
    }
    if (priceRange.max) {
      filtered = filtered.filter(p => p.price <= Number(priceRange.max));
    }
    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading amazing products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md">
          <div className="text-red-500 text-6xl mb-4">😔</div>
          <p className="text-red-500 text-lg font-semibold">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-6 bg-orange-500 text-white px-6 py-2 rounded-full hover:bg-orange-600 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-orange-500 to-pink-500 text-white py-16 mt-16">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center gap-2 text-sm mb-4">
            <Link to="/" className="hover:text-orange-200 transition">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/categories" className="hover:text-orange-200 transition">Categories</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-orange-200">{category?.name || "Category"}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {category?.name || "Category"} Collection
          </h1>
          <p className="text-lg md:text-xl text-orange-100 max-w-2xl">
            Discover our curated selection of premium {category?.name?.toLowerCase()} products
          </p>
          <div className="mt-6 flex gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
              <span className="font-semibold">{filteredProducts.length}</span> Products Available
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-72 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <Filter className="w-5 h-5 text-orange-500" />
                <h3 className="font-semibold text-lg">Filters</h3>
              </div>

              {/* Sort By */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Sort By</label>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="newest">Newest First</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name">Product Name</option>
                </select>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Price Range</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange({...priceRange, min: e.target.value})}
                    className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange({...priceRange, max: e.target.value})}
                    className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              {(priceRange.min || priceRange.max) && (
                <button
                  onClick={() => setPriceRange({ min: "", max: "" })}
                  className="text-sm text-orange-500 hover:text-orange-600"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {filteredProducts.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Products Found</h3>
                <p className="text-gray-500">Try adjusting your filters or check back later</p>
              </div>
            ) : (
              <>
                <div className="mb-6 flex justify-between items-center">
                  <p className="text-gray-600">Showing {filteredProducts.length} products</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProducts.map((product, index) => (
                    <div
                      key={product._id}
                      className="group bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={product.images?.[0] || "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=500"}
                          alt={product.name}
                          className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <button
                            onClick={() => toggleWishlist(product._id)}
                            className="bg-white rounded-full p-2 shadow-lg hover:bg-pink-50 transition"
                          >
                            <Heart 
                              className={`w-5 h-5 ${wishlist.includes(product._id) ? 'fill-pink-500 text-pink-500' : 'text-gray-600'}`}
                            />
                          </button>
                          <Link
                            to={`/product/${product._id}`}
                            className="bg-white rounded-full p-2 shadow-lg hover:bg-blue-50 transition"
                          >
                            <Eye className="w-5 h-5 text-gray-600" />
                          </Link>
                        </div>
                        {product.discount && (
                          <div className="absolute top-4 left-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-2 py-1 rounded-lg text-xs font-bold">
                            -{product.discount}% OFF
                          </div>
                        )}
                      </div>

                      <div className="p-5">
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-4 h-4 ${i < 4 ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          ))}
                          <span className="text-xs text-gray-500 ml-2">(128 reviews)</span>
                        </div>

                        <Link to={`/product/${product._id}`}>
                          <h3 className="font-semibold text-lg mb-2 text-gray-800 hover:text-orange-500 transition line-clamp-1">
                            {product.name}
                          </h3>
                        </Link>

                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {product.description || "Premium quality product with excellent features and durability"}
                        </p>

                        <div className="mb-4">
                          <div className="text-sm text-gray-500 mb-1">MRP: ₹{product.mrp || product.price}</div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl font-bold text-orange-500">₹{product.sp || product.price}</span>
                              {product.originalPrice && (
                                <span className="text-sm text-gray-400 line-through">₹{product.originalPrice}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-green-600 text-sm">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span>In Stock</span>
                            </div>
                          </div>
                          <div className="text-blue-600 font-semibold text-sm">PV: {product.pv || product.price}</div>
                        </div>

                        <button className="w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group">
                          <ShoppingBag className="w-5 h-5 group-hover:scale-110 transition" />
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryProducts;